import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApplicationItem } from "../../types";
import { IdentitySummary } from "./IdentitySummary";
import { FinancialSummary } from "./FinancialSummary";
import { BasicSummary } from "./BasicSummary";
import { HousingSummary } from "./HousingSummary";
import { IncomeSummary } from "./IncomeSummary";
import { DocumentSummary } from "./DocumentSummary";

type Props = {
  applicationId: string;
  item: ApplicationItem;
  highlight?: boolean;
};

export const ApplicationCard: React.FC<Props> = ({
  applicationId,
  item,
  highlight,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statusVariant = (status: ApplicationItem["status"]) => {
    switch (status) {
      case "created":
        return "primary";
      case "in-progress":
        return "info";
      case "completed":
        return "success";
    }
  };

  const badgeColorClass = (status: ApplicationItem["status"]) => {
    switch (status) {
      case "in-progress":
        return "bg-info";
      case "completed":
        return "bg-success";
      default:
        return "bg-primary";
    }
  };

  const btnColorClass = (status: ApplicationItem["status"]) => {
    switch (status) {
      case "created":
        return "bg-primary text-white hover:bg-primary-light";
      case "in-progress":
        return "bg-info text-white hover:bg-info/80";
      case "completed":
        return "bg-success text-white hover:bg-success/80";
    }
  };

  const statusLabel = t(`status.${item.status}`);
  const ctaLabel = t(`applicationCard.cta.${item.status}`);

  const renderDetails = () => {
    if (item.status !== "completed" || item.data === undefined) return null;
    switch (item.type) {
      case "basic":
        return <BasicSummary dob={item.data.dob} />;
      case "housing":
        return <HousingSummary entries={item.data} />;
      case "identity":
        return <IdentitySummary {...item.data} />;
      case "financial":
        return <FinancialSummary {...item.data} />;
      case "income":
        return <IncomeSummary entries={item.data} />;
      case "documents":
        return <DocumentSummary entries={item.data} />;
      default:
        return null;
    }
  };

  return (
    <div className="col">
      <div
        className={`relative h-full rounded-xl bg-white shadow-sm ${
          highlight ? "animate-pulse-gold" : ""
        }`}
      >
        {item.status !== "created" && (
          <span
            className={`absolute top-2 right-2 rounded-full px-2.5 py-0.5 text-xs font-medium text-white ${badgeColorClass(
              item.status
            )}`}
          >
            {statusLabel}
          </span>
        )}
        <div className="flex flex-col p-5">
          <h5 className="mb-4 text-base font-semibold text-primary">{t(item.title)}</h5>

          {item.status !== "completed" && (
            <p className="card-text">{t(item.description)}</p>
          )}

          {item.status === "completed" && (
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">{renderDetails()}</div>
          )}

          <div className="mt-auto">
            <button
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${btnColorClass(item.status)}`}
              onClick={() =>
                navigate(`/application/${applicationId}/${item.id}`)
              }
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
